import React, { Component } from 'react';
import gql from 'graphql-tag';
import PropTypes from 'prop-types';
import { compose, graphql, withApollo } from 'react-apollo';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { push } from 'react-router-redux';
import {
  Field,
  SubmissionError,
  formValueSelector,
  getFormSyncErrors,
  reduxForm
} from 'redux-form/immutable';
import { createStructuredSelector } from 'reselect';
import { setGtmUserId } from 'googleTagManagerMiddleware';
import { clearSessionError } from 'containers/App/actions';
import {
  makeSelectAuthRedirect,
  makeSelectLocation,
  makeSelectLocationError
} from 'containers/App/selectors';
import {
  DebugToggleOktaButton,
  emailRequiresOkta
} from 'containers/OktaLogin/utils';
import A11yFontIcon from 'components/A11yFontIcon';
import AuthBottom from 'components/AuthBottom';
import AuthTop from 'components/AuthTop';
import AuthWrapper from 'components/AuthWrapper';
import FormErrors from 'components/FormErrors';
import Input from 'components/Input';
import TextButton from 'components/TextButton';
import ViewablePasswordField from 'components/ViewablePasswordField';
import { setToken } from 'utils/auth';
import validators from 'utils/fieldValidators';
import fragments from 'utils/graphql/fragments';
import { GLOBAL_MESSAGES, RESEND_VERIFICATION_FAILED } from 'utils/messages';
import {
  ERROR_LOGIN_FAILED,
  SERVER_MESSAGES,
  USER_UNVERIFIED
} from 'utils/messages';
import { parseQuery } from 'utils/web';
import './LoginForm.css';

class LoginForm extends Component {
  constructor(props) {
    super(props);
    const { location: { state: locationState = {} } = {} } = props;
    this.state = {
      resendVerificationRequested: false,
      resendVerificationResult: null,
      oktaDisabled: !!locationState.disableOkta
    };
  }

  onSubmit = (values, dispatch, props) => {
    if (!this.checkForOktaRedirect(values.get('email'))) {
      this.setState({
        resendVerificationRequested: false,
        resendVerificationResult: null
      });
      dispatch(clearSessionError());
      return this.props.doLogin(values, dispatch, props);
    }
  };

  requestResend = () => {
    const email = this.props.formValues.email;
    if (email && !this.state.resendVerificationRequested) {
      this.setState({ resendVerificationRequested: true });
      this.props
        .resendVerification(email)
        .then(({ data }) => {
          const { resendVerificationEmail } = data;
          this.setState({ resendVerificationResult: resendVerificationEmail });
          return data;
        })
        .catch(e => {
          const result = { success: false, message: '' };
          this.setState({ resendVerificationResult: result });
        });
    }
  };

  handleEmailBlur = (event, newValue) => {
    this.checkForOktaRedirect(newValue);
  };

  checkForOktaRedirect(email) {
    if (!this.state.oktaDisabled && emailRequiresOkta(email)) {
      this.props.pushTo({
        pathname: '/okta',
        state: { email: email }
      });
      return true;
    } else {
      return false;
    }
  }

  onBack = () => {
    this.props.pushTo('/');
  };

  render() {
    const {
      handleSubmit,
      error,
      submitting,
      authError,
      formSyncErrors,
      formValues,
      location,
      submitFailed
    } = this.props;

    const params = parseQuery(location.search);
    let queryMessages = [];
    if (params.message) {
      const message = GLOBAL_MESSAGES[params.message];
      if (message) {
        queryMessages.push(message);
      }
    }

    return (
      <AuthWrapper
        aria-label="Sign In Form"
        logoName="login"
        onBack={this.onBack}
      >
        <form onSubmit={handleSubmit(this.onSubmit)} className="login-form">
          <h1 className="login-form-title">Sign In & PLAY!</h1>
          <AuthTop hideLogo>
            <div className="login-top">
              {authError && <FormErrors errors={[authError.message]} />}
              {formSyncErrors && submitFailed && (
                <FormErrors
                  errors={Object.values(formSyncErrors)}
                  showErrors={!!formValues.email || !!formValues.password}
                />
              )}
              {error && !submitting && (
                <FormErrors errors={this.renderVerificationLink(error)} />
              )}
              {!submitFailed && queryMessages.length > 0 && (
                <FormErrors
                  errors={queryMessages}
                  variant={params.variant || undefined}
                />
              )}
              <Field
                name="email"
                component={Input}
                type="email"
                label="Email Address"
                autoComplete="email username"
                readOnly={submitting}
                validate={[validators.emailRequired, validators.email]}
                onBlur={this.handleEmailBlur}
                subLabel={this.renderOktaLink(formValues.email)}
                autoFocus
                hideErrors
                hideWarnings
                required
              />
              <Field
                name="password"
                component={ViewablePasswordField}
                type="password"
                label="Password"
                autoComplete="current-password"
                readOnly={submitting}
                validate={validators.passwordRequired}
                hideErrors
                hideWarnings
                required
              />
              <div className="form-buttons-container">
                <button
                  className="btn button-color-green sign-in-btn"
                  disabled={submitting}
                  type="submit"
                >
                  {!submitting && <span>Sign in</span>}
                  {submitting && <span>Signing in...</span>}
                </button>
                <div className="link-container">
                  <Link className="link" to="/forgot-password">
                    Forgot Password
                  </Link>
                  <Link className="link" to="/support">
                    Support
                  </Link>
                </div>
              </div>
            </div>
          </AuthTop>
        </form>
        <AuthBottom className="login-form-bottom">
          <div> Don&apos;t have an account yet?</div>
          <Link to="/signup" className="btn button-color-green sign-up-btn">
            Sign up
          </Link>
        </AuthBottom>
        <DebugToggleOktaButton />
      </AuthWrapper>
    );
  }

  renderVerificationLink(errors) {
    const {
      resendVerificationRequested: requested,
      resendVerificationResult: result
    } = this.state;

    const unverified = errors.indexOf(SERVER_MESSAGES[USER_UNVERIFIED]);
    if (unverified >= 0) {
      errors = errors.slice();
      const error = errors[unverified];
      if (result) {
        errors[unverified] = result.success
          ? result.message
          : RESEND_VERIFICATION_FAILED;
      } else if (requested) {
        errors[unverified] = (
          <div>
            <div>{error}</div>
            <div>
              {'Resending '}
              <A11yFontIcon icon="spinner" pulse fixed alt="" />
            </div>
          </div>
        );
      } else {
        errors[unverified] = (
          <div>
            <div>{error}</div>
            <div>
              <TextButton underline onClick={this.requestResend}>
                Resend verification email
              </TextButton>
            </div>
          </div>
        );
      }
    }

    return errors;
  }

  renderOktaLink(email) {
    if (this.state.oktaDisabled) {
      return (
        <Link
          className="okta-link"
          to={{ pathname: '/okta', state: { email: email || null } }}
          tabIndex="-1"
        >
          Login with Okta
        </Link>
      );
    } else {
      return null;
    }
  }
}

// These prop types are passed down from the reduxForm HOC
LoginForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  error: PropTypes.arrayOf(PropTypes.string),
  submitting: PropTypes.bool,
  invalid: PropTypes.bool
};

const withReduxForm = reduxForm({
  form: 'loginForm'
});

const MUTATION_LOGIN = gql`
  mutation MyLogin($userCredentials: UserCredentials!) {
    login(userCredentials: $userCredentials) {
      success
      message
      sessionToken
      acceptedCurrentTermsOfService
      user {
        ...UserFields
      }
    }
  }
  ${fragments.user}
`;

const MUTATION_RESEND_VERIFICATION_EMAIL = gql`
  mutation MyResendVerification($input: ResendVerificationEmailInput!) {
    resendVerificationEmail(input: $input) {
      success
      message
    }
  }
`;

const withLogin = graphql(MUTATION_LOGIN, {
  props: ({ ownProps, mutate }) => ({
    doLogin: values => {
      return mutate({ variables: { userCredentials: values.toJS() } })
        .then(({ data }) => {
          if (!data.login.success) {
            let msg = data.login.message;
            if (SERVER_MESSAGES.hasOwnProperty(msg)) {
              msg = SERVER_MESSAGES[msg];
            }
            throw new SubmissionError({ _error: [msg] });
          }

          setToken(data.login.sessionToken);
          ownProps.client.recreateWebSocketConnection();
          setGtmUserId(data.login.user && data.login.user.userId);

          if (!data.login.acceptedCurrentTermsOfService) {
            ownProps.pushTo('/terms-of-service-updated');
          } else if (ownProps.authRedirect && ownProps.authRedirect.pathname) {
            // Redirect back to referrer
            ownProps.pushTo(ownProps.authRedirect.pathname);
          } else {
            ownProps.pushTo('/');
          }

          return data;
        })
        .catch(error => {
          if (error instanceof SubmissionError) {
            throw error;
          } else {
            if (error.message) {
              console.error(error.message);
            } else {
              console.error(JSON.stringify(error));
            }
            throw new SubmissionError({ _error: [ERROR_LOGIN_FAILED] });
          }
        });
    }
  })
});

const withResend = graphql(MUTATION_RESEND_VERIFICATION_EMAIL, {
  props: ({ ownProps, mutate }) => ({
    resendVerification: email =>
      mutate({
        variables: {
          input: { email: email }
        }
      })
  })
});

const selector = formValueSelector('loginForm');

const mapStateToProps = createStructuredSelector({
  authRedirect: makeSelectAuthRedirect(),
  authError: makeSelectLocationError(),
  formSyncErrors: getFormSyncErrors('loginForm'),
  formValues: state => selector(state, 'email', 'password'),
  location: makeSelectLocation()
});

const mapDispatchToProps = dispatch => ({
  pushTo: to => dispatch(push(to))
});

const withConnect = connect(mapStateToProps, mapDispatchToProps);

export default compose(
  withApollo,
  withConnect,
  withResend,
  withLogin,
  withReduxForm
)(LoginForm);
